<template>
  <div class="TextInput relative">
    <!-- * * * * actual input -->
    <input
      v-bind="attr"
      :id="inputId"
      ref="inputField"
      v-model="value"
      :type="inputType"
      class="input"
      :autocomplete="autocomplete"
      :placeholder="placeholder"
      :disabled="disabled || disableEdit"
      :readonly="readonly"
      :class="[ design, inputClass, {
        'cursor-not-allowed': disabled || disableEdit,
        '!pl-[36px]': isSearch,
        'outline outline-criticalDark': showErrorCombined,
        'hasLabel': showLabel,
        'invalid veeValidateFail' : showError,
        'bg-bg-inactive text-text-inactive': disableEdit
      }]"
      @focus="focused = true ; $emit('focus')"
      @blur="focused = false; $emit('blur')"
      @keydown.enter="$emit('enter')"
      @click="selectAll"
      @input="handleInput"
    >

    <!-- * * * * label -->
    <label
      :for="inputId"
      class="TextInputLabel absolute left-12
      type-sm leading-single
      select-none
      transition-all"
      :class="{

        'top-[16px]': design === 'lg',
        'top-[14px]': design === 'base',
        'top-[11px]': design === 'md',
        'top-[9px]': design === 'sm',
        'top-[7px]': design === 'xs',

        'top-[8px] type-xxs': (focused || isFilled || type === 'date') && design === 'lg',
        'top-[6px] type-xxs': (focused || isFilled || type === 'date') && design === 'base',
        'top-[3px] type-xxs': (focused || isFilled || type === 'date') && design === 'md',
        '!top-[2px] text-[9px]': (focused || isFilled || type === 'date') && design === 'sm',
        '!top-[2px] text-[7px]': (focused || isFilled || type === 'date') && design === 'xs',
        'text-criticalDark': showErrorCombined,
        'text-darker': !showErrorCombined,
        'left-0': !isSearch,
        'left-[36px]': isSearch,
      }"
      v-html="showLabel"
    />

    <!-- * * * * Search icon -->
    <div
      v-if="isSearch"
      class="absolute"
      :class="{
        'top-[15px]': design === 'lg',
        'top-[12px]': design === 'base',
        'top-[9px]': design === 'md',
        'top-[8px]': design === 'sm',
        'top-[5px]': design === 'xs',
      }"
    >
      <img
        src="/icons/search.svg"
        class="h-20 w-20 ml-12"
        alt=""
      >
    </div>
    <!-- * * * * suffix -->
    <div
      v-if="suffix !== ''"
      class="TextInputSuffix absolute  type-sm leading-single"
      :class="{
        'top-[16px] right-12': design === 'lg',
        'top-[13px] right-12': design === 'base',
        'top-[11px] right-8 type-xs': design === 'md',
        'top-[11px] right-[8px] type-xs': design === 'sm',
        'top-[7px] right-8 type-xs': design === 'xs',
      }"
    >
      {{ suffix }}
    </div>
    <button
      v-if="type === 'password'"
      type="button"
      class="absolute top-[14px] right-12"
      :class="{
        'right-12': showErrorCombined || showSuccess
      }"
      :aria-label="showPassword ? $t('wcag.arialabel.hidePassword') : $t('wcag.arialabel.showPassword')"
      :aria-pressed="showPassword"
      @click="showPassword = !showPassword"
    >
      <img
        :src="showPassword ? '/icons/eye-closed.svg' :'/icons/eye-open.svg'"
        class="w-20 h-20 relative"
        alt=""
      >
    </button>

    <div v-if="showErrorCombined" class="pointer-events-none select-none mt-8 h-16 relative">
      <div
        class="flex flex-row items-center gap-4"
        :class="{
          'absolute whitespace-nowrap': allowWideErrorMgs
        }"
      >
        <img
          src="/icons/exclamation-circle-critical.svg"
          class="w-16 h-16"
          alt=""
        >
        <div class="type-xs-medium leading-single text-criticalDark">{{ showErrorMsg }}</div>
      </div>
    </div>

    <div v-else-if="showSuccess" class="absolute top-12 right-0 pointer-events-none select-none">
      <img
        src="/icons/form-ok.svg"
        class="w-24 h-24"
        alt=""
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUiStore } from '~/store/ui';
const uiStore = useUiStore();

const value = defineModel<string|number|null>();

const props = withDefaults(defineProps<{
  id?: string;
  label?: string;
  placeholder?: string;
  showError?: boolean;
  showSuccess?: boolean;
  errors?: string[];
  type?: string;
  autocomplete?: string;
  serverName?: string;
  disabled?: boolean;
  isSearch?: boolean;
  focus?: boolean; // focus onMounted
  design?: 'lg' | 'base' | 'md' | 'sm' | 'xs';
  suffix?: string;
  // adds attributes to input field, example :attr="{min: '0', max: '100'}"
  attr?: {
    [key: string]: string;
  };
  allowWideErrorMgs?: boolean, // allows the error message to be wider then the input box. For small inputs
  selectAllOnClick?: boolean, // select content on click
  readonly?: boolean,
  inputClass?: string, // adds classes to input field
  disableEdit?: boolean
}>(), {
  label: '',
  placeholder: '',
  showError: false,
  showSuccess: false,
  errors: () => [],
  type: 'text',
  autocomplete: '',
  serverName: '',
  disabled: false,
  isSearch: false,
  design: 'lg',
  suffix: '',
  attr: () => ({}),
  allowWideErrorMgs: false,
  selectAllOnClick: false,
  readonly: false,
  inputClass: '',
});

defineEmits<{
  (e: 'focus'): void,
  (e: 'blur'): void,
  (e: 'enter'): void,
}>();

const _autoId = useId();
const inputId = computed(() => props.id ?? _autoId);
const focused = ref(false);
const showPassword = ref(false);

const inputType = computed(()=> {
  if (props.type === 'password' && showPassword.value) {
    return 'text';
  }
  return props.type;
});

const showErrorCombined = computed(()=> {
  return props.showError || (uiStore.formErrorMsg?.errors && uiStore.formErrorMsg?.errors[props.serverName]);
});

const showErrorMsg = computed(() => {
  // Server error
  if (uiStore.formErrorMsg?.errors && uiStore.formErrorMsg?.errors[props.serverName]) {
    return uiStore.formErrorMsg.errors[props.serverName][0];
  } else if (props.showError && props.errors.length) {
    return props.errors[0];
  }

  return ''; // Return an empty string if there are no errors
});
const showLabel = computed(()=> {
  const output = props.label;
  return output;
});

const inputField = useTemplateRef('inputField');
onMounted(()=> {
  if (props.focus) {
    setTimeout(()=> inputField.value?.focus(), 400);
  }
});

const selectAll = () => {
  if (props.selectAllOnClick) {
    inputField.value?.focus();
    inputField.value?.setSelectionRange(0, inputField.value.value?.length || 0);
  }
};

const isFilled = computed(()=> {
  if (value.value === null || value.value === undefined) {
    return false;
  }
  if (typeof value.value === 'string' && value.value === '') {
    return  false;
  }
  return true;

});

const handleInput = () => {
  // Don't replace comma - let parent handle conversion for calculations
};

</script>
